import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation } from 'react-router-dom';

import { ItemCardLiveBidButton as ItemCardBidButton } from '@/components/GridItemCard/Card/components/ItemCardBidButton/ItemCardLiveBidButton';
import ItemCardEstimate from '@/components/GridItemCard/Card/components/ItemCardEstimate/ItemCardEstimate';
import { ItemCardPriceRow } from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardMenu from '@/components/ItemCard/components/Menu/ItemCardMenu';
import { PriceRow } from '@/components/ItemCard/PriceRow';
import ItemPriceGatewayContainer from '@/components/ItemPriceGateway/ItemPriceGatewayContainer';
import { useIsMobileLayout } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import {
    getIsRegistered,
    getRegistrationApproved,
    getRegistrationApprovedWithLimit,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getIsItemSummaryTimedPlusBiddingOpen,
    getItemSummaryBidCount,
    getItemSummaryCatalogId,
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsSold,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { openRegisterForAuctionModal } from '@/redux/modules/modal';
import { getIsSearchPage, isRegisterButtonHiddenPage } from '@/utils/urls';

export type RegisterLinkOrPriceProps = {
    hasNewSimilarItemsIconCTA?: boolean;
    hideCardMenu?: boolean;
    itemId: number;
    linkHover?: boolean;
    onNavigate?: () => void;
    onRegisterForAuctionClicked?: () => void;
};

export const RegisterLinkOrPrice = ({
    hasNewSimilarItemsIconCTA = false,
    hideCardMenu = false,
    itemId,
    linkHover,
    onNavigate,
    onRegisterForAuctionClicked,
}: RegisterLinkOrPriceProps) => {
    const { pathname } = useLocation();
    const dispatch = useAppDispatch();
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const bidCount = useAppSelector((state) => getItemSummaryBidCount(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));
    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const approvedWithBidLimit = useAppSelector((state) => getRegistrationApprovedWithLimit(state, catalogId));
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));

    const isMobile = useIsMobileLayout();

    const isHiddenMenuPage = pathname.includes('/catalog/') || pathname.includes('/item/');
    const isHiddenButtonPage = isRegisterButtonHiddenPage(pathname);

    const isTimedPlusBiddingOpen = useAppSelector((state) => getIsItemSummaryTimedPlusBiddingOpen(state, itemId));

    // Still needed for the ItemCardMenu gates below; the register button uses isHiddenButtonPage.
    const isOnSearchPage = getIsSearchPage(pathname);

    let content: JSX.Element;

    if (isSold) {
        if (isLoggedIn) {
            content = (
                <div className="flex justify-between [grid-area:price]">
                    <ItemCardPriceRow
                        isHovered={linkHover}
                        itemId={itemId}
                        onNavigate={() => onNavigate?.()}
                        showBidCount={Boolean(bidCount)}
                    />
                    {!isHiddenMenuPage && !isOnSearchPage && !hasNewSimilarItemsIconCTA && (
                        <ItemCardMenu itemId={itemId} />
                    )}
                </div>
            );
        } else {
            content = (
                <ItemPriceGatewayContainer
                    className="text-base [&_button]:!text-primary-400 [&_button]:!no-underline hover:[&_button]:!underline"
                    hideIcon
                    itemId={itemId}
                />
            );
        }
    } else {
        const isOpenItem = !isPassed && !isPaused && isTimedPlusBiddingOpen;
        const isApprovedForOpenItem = isOpenItem && (approved || approvedWithBidLimit);
        const needsToRegister = !isPassed && !registered && !isHiddenMenuPage;
        let userActionButtonOrLink: JSX.Element;
        if (!isMobile) {
            if (isApprovedForOpenItem) {
                userActionButtonOrLink = <ItemCardBidButton itemId={itemId} />;
            } else if (needsToRegister) {
                userActionButtonOrLink = (
                    <Button
                        className="w-full"
                        onClick={() => {
                            onRegisterForAuctionClicked?.();
                            dispatch(
                                openRegisterForAuctionModal({
                                    catalogId,
                                    itemId,
                                })
                            );
                        }}
                        size="sm"
                        style={isOpenItem ? 'critical' : 'primary'}
                    >
                        <FormattedMessage id="itemPage.biddingInfo.timedPlus.notStarted.registerForAuction" />
                    </Button>
                );
            }
        } else {
            if (isApprovedForOpenItem) {
                userActionButtonOrLink = <ItemCardBidButton itemId={itemId} />;
            } else if (needsToRegister) {
                userActionButtonOrLink = (
                    <InlineButton
                        onClick={() => {
                            dispatch(
                                openRegisterForAuctionModal({
                                    catalogId,
                                    itemId,
                                })
                            );
                        }}
                    >
                        <FormattedMessage id="itemPage.biddingInfo.timedPlus.notStarted.registerForAuction" />
                    </InlineButton>
                );
            }
        }
        content = (
            <div className="flex flex-col">
                <ItemCardEstimate itemId={itemId} />
                <div className="flex justify-between [grid-area:price]">
                    <PriceRow
                        className="!mt-0 mb-[10px]"
                        isCatalogCard
                        itemId={itemId}
                        onNavigate={() => onNavigate?.()}
                    />
                    {!isHiddenMenuPage && !isOnSearchPage && !hideCardMenu && !hasNewSimilarItemsIconCTA && (
                        <ItemCardMenu itemId={itemId} />
                    )}
                </div>
                {!isHiddenButtonPage && <div className="flex h-full items-end">{userActionButtonOrLink}</div>}
            </div>
        );
    }

    return <div className="relative mt-2xs h-full [grid-area:registerlinkorprice]">{content}</div>;
};
